import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import livly from "../../assets/logoLC.jpeg";
import { NavLink, useLocation } from "react-router-dom";
import {
  isNavItemActive,
  navlinks,
  privateNavlinks,
} from "shared/utils/navRoutes";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { isAdmin } from "shared/api/IsAdmin";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import TenantSwitcher from "components/Tenants/TenantSwitcher";
import { useFeatureFlags } from "shared/context/FeatureFlags";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "components/ui/collapsible";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from "components/ui/sheet";

const Navbar = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const user = UseLivlyLeaseUser();
  const privateNavs = privateNavlinks();
  const [isExpandable, setIsExpendable] = useState(false);
  const { flags } = useFeatureFlags();
  const { pathname } = useLocation();

  return (
    <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
      {/* mobile nav start */}
      <SheetContent
        className="flex w-full max-w-xs flex-col gap-0 bg-white p-0 sm:max-w-xs"
        showCloseButton={false}
        side="left"
      >
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <SheetDescription className="sr-only">
          Navigate through LivlyConnect.
        </SheetDescription>
        <div className="flex justify-end pt-2 pr-2">
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            onClick={() => setSidebarOpen(false)}
          >
            <span className="sr-only">Close sidebar</span>
            <FontAwesomeIcon
              aria-hidden="true"
              className="h-6 w-6 text-gray-500"
              icon="xmark"
            />
          </button>
        </div>
        <div className="h-0 flex-1 overflow-y-auto pt-5 pb-4">
          <div className="flex shrink-0 items-center px-4">
            <div>
              <div className="flex items-center">
                <img
                  src={livly}
                  alt="logo"
                  width={25}
                  style={{ marginRight: "10px" }}
                />
                <p>
                  <strong>LivlyConnect.</strong>
                </p>
              </div>
              <div className="float-right mt-1 mr-1 h-[2px] w-12 bg-[#5FA5F8]" />
            </div>
          </div>
          <nav className="mt-5 space-y-1 px-2">
            {navlinks.map((item) => {
              const isActive = isNavItemActive(pathname, item);

              return (
                <NavLink
                  key={item.name}
                  to={item.link}
                  onClick={() => setSidebarOpen(false)}
                  className={`${
                    isActive
                      ? "bg-blue-100 text-blue-700"
                      : "grayscale hover:bg-blue-100 hover:bg-opacity-75"
                  } group flex items-center rounded-md px-2 py-2 text-base font-medium`}
                >
                  <FontAwesomeIcon
                    aria-hidden="true"
                    className="mr-4 h-6 w-6 shrink-0 text-blue-700"
                    icon={item.icon as IconProp}
                  />
                  {item.name}
                </NavLink>
              );
            })}
            <hr />
            {isAdmin(user) && (
              <>
                <Collapsible
                  onOpenChange={(nextOpen) => setIsExpendable(nextOpen)}
                  open={isExpandable}
                >
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="mt-5 flex w-full items-center justify-start px-2 grayscale"
                    >
                      <FontAwesomeIcon
                        aria-hidden="true"
                        className="mr-4 h-6 w-6 shrink-0"
                        icon="user-gear"
                      />
                      <strong className="mr-4">Admin</strong>
                      <span
                        className={`transition-transform duration-300 ${
                          isExpandable ? "rotate-90" : "rotate-0"
                        }`}
                      >
                        <FontAwesomeIcon
                          aria-hidden="true"
                          className="h-3 w-3 shrink-0"
                          icon="chevron-right"
                        />
                      </span>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div>
                      {privateNavs.map((item) => {
                        const isActive = isNavItemActive(pathname, item);

                        return (
                          <NavLink
                            key={item.name}
                            to={item.link}
                            onClick={() => setSidebarOpen(false)}
                            className={`${
                              isActive
                                ? "bg-blue-100 text-blue-700"
                                : "grayscale hover:bg-blue-100 hover:bg-opacity-75"
                            } group flex items-center rounded-md px-2 py-2 text-base font-medium`}
                          >
                            <FontAwesomeIcon
                              aria-hidden="true"
                              className="mr-4 h-6 w-6 shrink-0 text-blue-700"
                              icon={item.icon as IconProp}
                            />
                            {item.name}
                          </NavLink>
                        );
                      })}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
                <hr />
              </>
            )}
            <NavLink
              to="/logout"
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) =>
                `${
                  isActive
                    ? "bg-blue-100 text-blue-700"
                    : "grayscale hover:bg-blue-100 hover:bg-opacity-75"
                } group flex items-center rounded-md px-2 py-2 text-base font-medium`
              }
            >
              <FontAwesomeIcon
                aria-hidden="true"
                className="mr-4 h-6 w-6 shrink-0 text-blue-700"
                icon="right-from-bracket"
              />
              Logout
            </NavLink>
          </nav>
        </div>
      </SheetContent>
      {/*  mobile nav end */}
      <div className="flex justify-between items-center md:justify-end mb-[10px]">
        {/* mobile nav icon start */}
        <div className="sticky top-0 z-10 pt-1 pl-1 bg-stone-100 sm:pl-3 sm:pt-3 md:hidden">
          <SheetTrigger asChild>
            <button
              type="button"
              className="-ml-0.5 -mt-0.5 inline-flex h-12 w-12 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
            >
              <span className="sr-only">Open sidebar</span>
              <FontAwesomeIcon
                icon="bars"
                className="w-6 h-6"
                aria-hidden="true"
              />
            </button>
          </SheetTrigger>
        </div>
        {/* mobile nav icon end*/}
        <div
          className={`flex items-center gap-4${flags.sidebar ? " md:hidden" : ""}`}
        >
          <TenantSwitcher />
        </div>
      </div>
    </Sheet>
  );
};

export default Navbar;
